import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import {
  PrettySelectComponent,
  PrettySelectOption,
} from '../../../shared/components/ui/pretty-select/pretty-select.component';
import { FormMode } from '../../../shared/types/forms.types';
import {
  PaymentMethod,
  PaymentMethodType,
  PaymentMethodWriteRequest,
} from '../../../shared/types/payment-method.types';

const TYPE_OPTIONS: PrettySelectOption<PaymentMethodType>[] = [
  { value: 'WALLET', label: 'Wallet mobile', hint: 'Wave, Orange Money, Free Money…' },
  { value: 'CARD', label: 'Carte bancaire', hint: 'Visa, MasterCard' },
  { value: 'CASH', label: 'Espèces', hint: 'Règlement en liquide au retrait (serviceCode CASH)' },
];

@Component({
  selector: 'app-payment-method-form',
  imports: [CommonModule, ReactiveFormsModule, PrettySelectComponent],
  templateUrl: './payment-method-form.component.html',
})
export class PaymentMethodFormComponent implements OnChanges {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  @Input() mode: FormMode = 'create';
  @Input() initial: PaymentMethod | null = null;
  @Input() submitting = false;
  @Input() serverError: string | null = null;

  @Output() readonly save = new EventEmitter<PaymentMethodWriteRequest>();
  @Output() readonly cancel = new EventEmitter<void>();

  readonly typeOptions = TYPE_OPTIONS;

  readonly form = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(80)]],
    serviceCode: ['', [Validators.required, Validators.maxLength(80)]],
    imageUrl: [''],
    type: ['WALLET' as PaymentMethodType, [Validators.required]],
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initial']) {
      this.applyInitial();
    }
  }

  private applyInitial(): void {
    const p = this.initial;
    this.form.reset({
      name: p?.name ?? '',
      serviceCode: p?.serviceCode ?? '',
      imageUrl: p?.imageUrl ?? '',
      type: p?.type ?? 'WALLET',
    });
  }

  onSubmit(): void {
    if (this.form.invalid || this.submitting) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    this.save.emit({
      name: raw.name.trim(),
      serviceCode: raw.serviceCode.trim(),
      imageUrl: raw.imageUrl.trim() || null,
      type: raw.type,
    });
  }

  onCancel(): void {
    this.cancel.emit();
  }

  showError(name: keyof typeof this.form.controls): boolean {
    const c = this.form.controls[name];
    return c.invalid && (c.touched || c.dirty);
  }
}
